import React, { useEffect, useRef } from 'react';
import Slider from 'rc-slider';
import type { SliderProps } from 'rc-slider';
import raf from 'rc-util/lib/raf';
import Tooltip from 'rc-tooltip';
import './index.scss';

const HandleTooltip = (props: {
  value: number;
  children: React.ReactElement;
  visible: boolean;
  tipFormatter?: (value: number) => React.ReactNode;
}) => {
  const {
    value,
    children,
    visible,
    tipFormatter = (val) => `${val}`,
    ...restProps
  } = props;

  const tooltipRef = useRef<any>();
  const rafRef = useRef<number | null>(null);

  function cancelKeepAlign() {
    raf.cancel(rafRef.current!);
  }

  function keepAlign() {
    rafRef.current = raf(() => {
      tooltipRef.current?.forceAlign();
    });
  }

  useEffect(() => {
    if (visible) {
      keepAlign();
    } else {
      cancelKeepAlign();
    }

    return cancelKeepAlign;
  }, [value, visible]);

  return (
    <Tooltip
      placement="top"
      overlay={tipFormatter(value)}
      overlayInnerStyle={{
        minHeight: 'auto',
        fontWeight: 700,
        fontSize: 14,
        height: '1rem',
      }}
      ref={tooltipRef}
      visible={visible}
      {...restProps}
    >
      {children}
    </Tooltip>
  );
};

export const handleRender: SliderProps['handleRender'] = (node, props) => {
  return (
    <HandleTooltip value={props.value} visible={props.dragging}>
      {node}
    </HandleTooltip>
  );
};

const TooltipSlider = ({
  tipFormatter,
  tipProps,
  max,
  min,
  marks,
  step,
  defaultValue,
  ...props
}: TooltipSliderProps &
  SliderProps & {
    tipFormatter?: (value: number) => React.ReactNode;
    tipProps?: any;
  }) => {
  const tipHandleRender: SliderProps['handleRender'] = (node, handleProps) => {
    return (
      <HandleTooltip
        value={handleProps.value}
        visible={props.isShowTooltip}
        tipFormatter={tipFormatter}
        {...tipProps}
      >
        {node}
      </HandleTooltip>
    );
  };

  return (
    <div>
      <div className="custom-slider">
        <Slider
          {...props}
          handleRender={tipHandleRender}
          min={min}
          max={max}
          marks={marks}
          step={step}
          defaultValue={defaultValue}
          railStyle={{ backgroundColor: '#666', height: 5 }}
          trackStyle={{ backgroundColor: 'white', height: 5 }}
          handleStyle={{
            borderColor: 'white',
            height: 24,
            width: 6,
            marginTop: -10,
            backgroundColor: 'white',
            borderRadius: 0,
            opacity: 1,
          }}
        />
      </div>
    </div>
  );
};

export default TooltipSlider;

interface TooltipSliderProps {
  isShowTooltip?: boolean;
}
